import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import Drawer from "../../../../components/Drawer";
import Form from "./Form";
import BulkActions from "../../components/BulkActions";
import Topbar from "../../components/Topbar";
import Actions from "../../../../components/NTable/Actions";
import { useColumns } from "./columns";
import useSettingsCRUDHook from "../../hook";
import { useDrawerProps } from "../../helpers/useDrawerProps";
import { useDeleteModal } from "../../helpers/useDeleteModal";
import { useManageForm } from "../../helpers/useManageForm";
import { useFetchResource } from "../../helpers/useFetchResource";
import { useAccess, useActionWithAccess } from "../../../../utils/hooks/access";
import { useVisibleColumnKeys } from "../../helpers/useVisibleColumnKeys";
import { useOnExport } from "../../helpers/useOnExport";
import { useImportModal } from "../../helpers/useImportModal";
import { useQueryFilters } from "../../helpers/useQueryFilters";
import { useNewTable } from "../../../../components/NTable/hook";
import NTable from "../../../../components/NTable";
import { useVisible } from "../../../../utils/hooks/useVisible";
import HistoryDrawer from "./HistoryDrawer";

function CalculationGroup() {
	const { t } = useTranslation();
	const {
		visible: isHistoryVisible,
		open: _openHistory,
		close: closeHistory,
		selected: selectedRow
	} = useVisible();
	
	const openHistory = useActionWithAccess(_openHistory, "canViewAudits");

	const {
		state: { mode, data, totalItems, loading, submitting, error },
		selected,
		fetch,
		createSubmit,
		updateSubmit,
		goToHelp,
		goToView,
		goToCreate,
		goToEdit,
		goToClone,
		remove,
		batchRemove,
		setData,
		settingsExport,
		loadingExport,
	} = useSettingsCRUDHook("/calculation-group");

	const { hasAccess: canAdd } = useAccess(
		"settings.payPolicies.calculationGroup.canCreate",
	);

	const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
		message: (data) =>
			Array.isArray(data)
				? t("do-you-want-to-delete-calculation-groups", {
					count: data?.length
				})
				: t("do-you-want-to-delete-calculation-group"),
		onConfirm: (data) =>
			Array.isArray(data) ? batchRemove(data) : remove(data),
		permission: "settings.payPolicies.calculationGroup.canDelete",
	});
	const { unarchiveModal, isFormOpen, formProps } = useManageForm({
		mode,
		selected,
		createSubmit,
		updateSubmit,
	});

	const columns = useColumns({
		edit: useActionWithAccess(
			goToEdit,
			"settings.payPolicies.calculationGroup.canEdit",
		),
		remove: openDeleteModal,
		clone: useActionWithAccess(
			goToClone,
			"settings.payPolicies.calculationGroup.canCreate",
		),
		history: openHistory,
	});

	const table = useNewTable({
		data,
		columns,
		loading,
		totalItems,
		sessionFiltersKey: "calculationGroups",
	});

	const queryFilters = useQueryFilters(table);

	const { open: drawerOpen, title: drawerTitle } = useDrawerProps({
		mode,
		help: t("help"),
		view: t("view-calculation-group"),
		create: t("add-calculation-group"),
		edit: t("edit-calculation-group"),
		clone: t("clone-calculation-group"),
	});

	useFetchResource({ fetch, state: table.getState() });

	const visibleColumnKeys = useVisibleColumnKeys(table);

	const onExport = useOnExport({
		url: "/export/calculation-groups",
		exportCall: settingsExport,
		queryFilters,
		visibleColumnKeys
	});

	const { modal: importModal } = useImportModal({
		importUrl: "/calculation-groups/import",
		exportUrl: "/export/calculation-groups",
		setData,
		exportCall: settingsExport,
		loading: loading || loadingExport,
	});

	return (
		<div className="d-flex h-100">
			<Card className="flex-fill mb-0">
				<Topbar
					title={t("calculation-groups")}
					add={canAdd && goToCreate}
					help={goToHelp}
					// importFile={openImportModal}
					onExport={onExport}
					loading={loading || loadingExport}
					history={!!openHistory && (() => openHistory())}
				/>

				<Card
					className="mb-auto"
					style={{ maxHeight: "calc(100% - 156px)" }}
				>
					<Actions
						filters={table.getState().columnFilters}
						setFilters={table.setColumnFilters}
						columns={table.getAllColumns()}
					>
						<BulkActions
							rows={table.getState().rowSelection}
							batchRemove={openDeleteModal}
						/>
					</Actions>

					<NTable table={table} />
				</Card>
			</Card>

			<Drawer open={drawerOpen} close={goToView} title={drawerTitle}>
				{isFormOpen ? (
					<Form
						{...formProps}
						error={error}
						loading={submitting}
						close={goToView}
					/>
				) : (
					<>{t("help")}</>
				)}
			</Drawer>

			{isHistoryVisible && (
				<HistoryDrawer open={isHistoryVisible} close={closeHistory} id={selectedRow} />
			)}

			{importModal}

			{deleteModal}

			{unarchiveModal}
		</div>
	);
}

export default CalculationGroup;
